import React, { useEffect, useState } from "react";
import { Reorder } from "framer-motion";
import api, { API, apiError, fileUrl } from "@/lib/api";
import { PageHeader, btn, input, Field } from "@/components/kit";
import { toast } from "sonner";
import { Loader2, Upload, GripVertical } from "lucide-react";

const COLORS = [
  { l: "Cobalt", v: "221 83% 53%" },
  { l: "Emerald", v: "160 84% 39%" },
  { l: "Violet", v: "262 83% 58%" },
  { l: "Rose", v: "347 77% 50%" },
  { l: "Amber", v: "38 92% 50%" },
  { l: "Slate", v: "222 47% 11%" },
];

const TIMEZONES = [
  "UTC", "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "Europe/Athens", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Sao_Paulo", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo",
  "Australia/Sydney", "Pacific/Auckland",
];

export default function OrgSettings() {
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [stripeKey, setStripeKey] = useState(null); // null = untouched, string = new value to save
  const [verifying, setVerifying] = useState(false);
  const [payStatus, setPayStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const loadPayStatus = () => api.get("/orgs/me/stripe-status").then((r) => setPayStatus(r.data)).catch(() => {});
  const [payouts, setPayouts] = useState(null);
  useEffect(() => {
    loadPayStatus();
    api.get("/orgs/me/payouts").then((r) => setPayouts(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe") === "return") toast.success("Stripe onboarding updated — checking status…");
  }, []);

  const connectStripe = async () => {
    setConnecting(true);
    try { const { data } = await api.post("/orgs/me/stripe/connect"); window.location.href = data.url; }
    catch (e) { toast.error(apiError(e)); setConnecting(false); }
  };
  const sendDigestPreview = async () => {
    setPreviewing(true);
    try { const { data } = await api.post("/orgs/me/digest-preview"); toast.success(`${data.sample ? "Sample" : "Preview"} digest sent to ${data.to}`); }
    catch (e) { toast.error(apiError(e)); } finally { setPreviewing(false); }
  };

  const verifyStripe = async () => {
    setVerifying(true);
    try {
      const payload = (stripeKey && stripeKey.startsWith("sk_")) ? { key: stripeKey } : {};
      const { data } = await api.post("/orgs/me/verify-stripe", payload);
      toast.success(`Stripe key works — ${data.mode} mode${data.account ? ` (${data.account})` : ""}`);
      api.get("/orgs/me/stripe-status").then((r) => setPayStatus(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e)); } finally { setVerifying(false); }
  };

  useEffect(() => {
    api.get("/orgs/me").then((r) => {
      const o = r.data;
      setOrg(o);
      api.get("/flows").then((fr) => {
        const cats = [...new Set(fr.data.filter((f) => f.public && (f.category || "").trim()).map((f) => f.category.trim()))];
        const saved = (o.hub_section_order || []).filter((c) => cats.includes(c));
        const rest = cats.filter((c) => !saved.includes(c));
        setSections([...saved, ...rest]);
      }).catch(() => {});
    }).catch(() => {});
  }, []);

  const set = (k, v) => setOrg({ ...org, [k]: v });

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
  };

  const onHubImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("hub_image", reader.result);
    reader.readAsDataURL(file);
  };

  const onSocialImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("social_image", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/orgs/me", { name: org.name, slug: org.slug, industry: org.industry, primary_color: org.primary_color, logo: org.logo, webhook_url: org.webhook_url || "", hub_image: org.hub_image || "", hub_intro: org.hub_intro || "", hub_headline: org.hub_headline || "", hub_section_order: sections, social_image: org.social_image || "", signup_notify_mode: org.signup_notify_mode || "instant", digest_hour: org.digest_hour ?? 9, digest_timezone: org.digest_timezone || "UTC", ...(stripeKey !== null ? { stripe_secret_key: stripeKey } : {}) });
      setStripeKey(null);
      setOrg(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!org) return <div className="animate-pulse text-muted-foreground overline">Loading…</div>;

  return (
    <div className="max-w-2xl">
      <PageHeader title="Organization settings" subtitle="Branding your clients will see in their portal." />

      <div className="bg-card border border-border p-6 space-y-6">
        <div className="border border-accent/40 bg-accent/5 p-4">
          <p className="text-sm font-medium">Your public onboarding hub</p>
          <p className="text-xs text-muted-foreground mt-1">Share this link so clients can self-start any form you mark “public”.</p>
          <div className="flex items-center gap-2 mt-2">
            <code className="text-xs bg-background border border-border px-2 py-1.5 flex-1 truncate" data-testid="hub-url">{window.location.origin}/hub/{org.slug}</code>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/hub/${org.slug}`); toast.success("Hub link copied"); }} className={btn.outline} data-testid="copy-hub-url">Copy</button>
            <a href={`/hub/${org.slug}`} target="_blank" rel="noreferrer" className={btn.ghost}>Open</a>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Sharing on WhatsApp, Slack or LinkedIn? Use the rich-preview link — it shows your headline, intro and image.</p>
          <div className="flex items-center gap-2 mt-2">
            <code className="text-xs bg-background border border-border px-2 py-1.5 flex-1 truncate" data-testid="hub-share-url">{API}/public/hub/{org.slug}/share</code>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(`${API}/public/hub/${org.slug}/share`); toast.success("Share link copied"); }} className={btn.outline} data-testid="copy-hub-share-url">Copy</button>
          </div>
        </div>
        <Field label="Organization name"><input className={input} value={org.name} onChange={(e) => set("name", e.target.value)} data-testid="settings-name" /></Field>
        <Field label="Hub address (slug)" hint="Your public hub lives at /hub/your-slug. Lowercase letters, numbers and dashes. Changing this breaks previously shared links.">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground shrink-0">/hub/</span>
            <input className={input} value={org.slug || ""} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} data-testid="settings-slug" placeholder="your-business" />
          </div>
        </Field>
        <Field label="Industry"><input className={input} value={org.industry} onChange={(e) => set("industry", e.target.value)} data-testid="settings-industry" /></Field>

        <Field label="Logo" hint="Shown at the top of your client onboarding portal.">
          <div className="flex items-center gap-4">
            {org.logo ? <img src={fileUrl(org.logo)} alt="logo" className="h-12 border border-border" /> : <div className="h-12 w-12 bg-secondary grid place-items-center text-xs text-muted-foreground">None</div>}
            <label className={`${btn.outline} cursor-pointer`}><Upload className="h-4 w-4" /> Upload<input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="settings-logo-upload" /></label>
          </div>
        </Field>

        <Field label="Accent color">
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button key={c.v} onClick={() => set("primary_color", c.v)} data-testid={`color-${c.l.toLowerCase()}`}
                className={`h-9 w-9 border-2 ${org.primary_color === c.v ? "border-foreground" : "border-transparent"}`}
                style={{ background: `hsl(${c.v})` }} title={c.l} />
            ))}
          </div>
        </Field>

        <Field label="Outbound webhook URL" hint="We POST a JSON payload here when a client finishes onboarding — connect Zapier, Make, HubSpot or Notion.">
          <input className={input} value={org.webhook_url || ""} onChange={(e) => set("webhook_url", e.target.value)} placeholder="https://hooks.zapier.com/…" data-testid="settings-webhook" />
        </Field>

        <Field label="Hub hero image" hint="Large banner shown at the top of your public hub page.">
          <div className="flex items-center gap-4">
            {org.hub_image ? <img src={org.hub_image.startsWith("data:") ? org.hub_image : fileUrl(org.hub_image)} alt="hub" className="h-16 w-28 object-cover border border-border" /> : <div className="h-16 w-28 bg-secondary grid place-items-center text-xs text-muted-foreground">None</div>}
            <label className={`${btn.outline} cursor-pointer`}><Upload className="h-4 w-4" /> Upload<input type="file" accept="image/*" className="hidden" onChange={onHubImage} data-testid="settings-hub-image" /></label>
          </div>
        </Field>
        <Field label="Social preview image" hint="Shown when your hub link is shared on WhatsApp, Slack or LinkedIn. Ideal size 1200×630. Falls back to the hero image or logo if left empty.">
          <div className="flex items-center gap-4">
            {org.social_image ? <img src={org.social_image.startsWith("data:") ? org.social_image : fileUrl(org.social_image)} alt="social preview" className="h-16 w-28 object-cover border border-border" data-testid="settings-social-preview" /> : <div className="h-16 w-28 bg-secondary grid place-items-center text-xs text-muted-foreground">None</div>}
            <label className={`${btn.outline} cursor-pointer`}><Upload className="h-4 w-4" /> Upload<input type="file" accept="image/*" className="hidden" onChange={onSocialImage} data-testid="settings-social-image" /></label>
          </div>
        </Field>
        <Field label="Hub headline" hint="Big heading shown on your public hub. Leave blank to use “Get started with {name}”.">
          <input className={input} value={org.hub_headline || ""} onChange={(e) => set("hub_headline", e.target.value)} data-testid="settings-hub-headline" placeholder={`Get started with ${org.name}`} />
        </Field>
        <Field label="Hub intro text" hint="Short welcome shown under the hub heading.">
          <textarea rows={2} className={input} value={org.hub_intro || ""} onChange={(e) => set("hub_intro", e.target.value)} data-testid="settings-hub-intro" placeholder="Welcome! Choose a form below to get started." />
        </Field>

        {sections.length > 1 && (
          <Field label="Hub section order" hint="Drag to set the order sections appear on your public hub.">
            <Reorder.Group axis="y" values={sections} onReorder={setSections} className="space-y-2" data-testid="section-order-list">
              {sections.map((s) => (
                <Reorder.Item key={s} value={s} className="flex items-center gap-3 bg-background border border-border px-3 py-2 cursor-grab active:cursor-grabbing" data-testid={`section-order-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{s}</span>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          </Field>
        )}

        <Field label="New signup notifications" hint="Choose how you're notified when someone self-starts onboarding from your public hub.">
          <select className={input} value={org.signup_notify_mode || "instant"} onChange={(e) => set("signup_notify_mode", e.target.value)} data-testid="settings-notify-mode">
            <option value="instant">Email me for every new signup</option>
            <option value="digest">One daily digest summary</option>
            <option value="both">Both — instant emails and a daily digest</option>
          </select>
        </Field>

        {["digest", "both"].includes(org.signup_notify_mode || "instant") && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Daily digest time" hint="When your daily signup digest is sent, in the timezone below.">
              <select className={input} value={org.digest_hour ?? 9} onChange={(e) => set("digest_hour", parseInt(e.target.value))} data-testid="settings-digest-hour">
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                ))}
              </select>
            </Field>
            <Field label="Timezone">
              <select className={input} value={org.digest_timezone || "UTC"} onChange={(e) => set("digest_timezone", e.target.value)} data-testid="settings-digest-tz">
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
              </select>
            </Field>
            <div className="col-span-2">
              <button type="button" onClick={sendDigestPreview} disabled={previewing} className={btn.outline} data-testid="settings-digest-preview">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send me a sample digest
              </button>
            </div>
          </div>
        )}

        <Field label="Accept payments (Stripe Connect)" hint="Recommended — link your own Stripe account through Stripe's secure onboarding. No secret keys to copy.">
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={connectStripe} disabled={connecting} className={btn.primary} data-testid="settings-stripe-connect">
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {payStatus?.method === "connect" ? "Continue Stripe setup" : "Connect with Stripe"}
            </button>
            {payStatus?.method === "connect" && (
              payStatus.error ? <span className="text-sm text-destructive">{payStatus.error}</span>
              : payStatus.charges_enabled ? <span className="text-sm text-emerald-600 inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Connected & ready ({payStatus.mode})</span>
              : <span className="text-sm text-amber-600 inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Connected — finish onboarding to accept payments</span>
            )}
          </div>
        </Field>

        {payouts && payouts.payments > 0 && (
          <div className="grid grid-cols-3 gap-3" data-testid="payout-report">
            <div className="bg-background border border-border p-4"><p className="overline text-muted-foreground">Collected</p><p className="text-2xl font-light tracking-tighter mt-1">£{payouts.collected_gbp.toLocaleString()}</p></div>
            <div className="bg-background border border-border p-4"><p className="overline text-muted-foreground">Fees deducted</p><p className="text-2xl font-light tracking-tighter mt-1">£{payouts.fees_gbp.toLocaleString()}</p></div>
            <div className="bg-background border border-border p-4"><p className="overline text-muted-foreground">Net to you</p><p className="text-2xl font-light tracking-tighter mt-1 text-accent">£{payouts.net_gbp.toLocaleString()}</p></div>
          </div>
        )}

        <Field label="Or add a Stripe secret key manually" hint="Alternative to Connect. Add your own Stripe secret key so payment/deposit steps charge into YOUR Stripe account. Starts with sk_. Leave as-is to keep the current key.">
          <div className="flex items-center gap-2">
            <input className={input} type="password" autoComplete="off"
              value={stripeKey !== null ? stripeKey : (org.stripe_configured ? `•••• •••• ${org.stripe_key_last4}` : "")}
              onFocus={() => { if (stripeKey === null) setStripeKey(""); }}
              onChange={(e) => setStripeKey(e.target.value)}
              placeholder="sk_live_… or sk_test_…" data-testid="settings-stripe-key" />
            {org.stripe_configured && stripeKey === null && (
              <button type="button" onClick={() => setStripeKey("")} className={btn.ghost + " text-destructive shrink-0"} data-testid="settings-stripe-clear">Remove</button>
            )}
          </div>
          {(org.stripe_configured || (stripeKey && stripeKey.startsWith("sk_"))) && (
            <button type="button" onClick={verifyStripe} disabled={verifying} className={btn.outline + " mt-2"} data-testid="settings-stripe-verify">
              {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Verify key
            </button>
          )}
          {payStatus && payStatus.configured && payStatus.method !== "connect" && (
            <div className="mt-2 text-sm" data-testid="stripe-payout-status">
              {payStatus.error ? (
                <span className="inline-flex items-center gap-1.5 text-destructive"><span className="h-2 w-2 rounded-full bg-destructive" /> {payStatus.error}</span>
              ) : payStatus.charges_enabled ? (
                <span className="inline-flex items-center gap-1.5 text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Ready to accept payments ({payStatus.mode} mode){payStatus.payouts_enabled ? " · payouts enabled" : ""}</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-amber-600"><span className="h-2 w-2 rounded-full bg-amber-500" /> Connected, but this Stripe account can't accept live payments yet — finish onboarding in your Stripe dashboard.</span>
              )}
            </div>
          )}
        </Field>

        <button onClick={save} disabled={saving} className={btn.primary} data-testid="settings-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save changes
        </button>
      </div>
    </div>
  );
}
